import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { fetchUserAttributes, fetchAuthSession } from '@aws-amplify/auth';
import { Link } from 'react-router-dom';
import { resolveSessionForType } from '../../auth/resolveSession';
import WelcomeEditor from '../shared/WelcomeEditor';

const SAVE_ENDPOINT = 'https://h7ujkfcwd3.execute-api.us-west-2.amazonaws.com/default/save-welcome-bio';
const empty = { contentFormat: 'text', welcomeHeader: '', tagline: '', profileImageUrl: '', welcomeMessage: '', ctaButtons: [], sections: [] };
const text = value => typeof value === 'string' ? value : '';
function normalize(data, fallback) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Invalid saved page');
  for (const key of ['welcomeHeader', 'tagline', 'profileImageUrl', 'welcomeMessage']) {
    if (data[key] != null && typeof data[key] !== 'string') throw new Error('Invalid saved field');
  }
  for (const key of ['ctaButtons', 'sections']) {
    if (data[key] != null && (!Array.isArray(data[key]) || data[key].some(item => !item || typeof item !== 'object' || Array.isArray(item)))) throw new Error('Invalid saved list');
    const fields = key === 'ctaButtons' ? ['label', 'url'] : ['title', 'text', 'imageUrl'];
    if (data[key]?.some(item => fields.some(field => item[field] != null && typeof item[field] !== 'string'))) throw new Error('Invalid saved list field');
  }
  return { contentFormat: data.contentFormat === 'text' || !/<\/?[a-z][\s\S]*>/i.test(data.welcomeMessage || '') ? 'text' : 'legacy', welcomeHeader: data.welcomeHeader ?? fallback, tagline: data.tagline || '', profileImageUrl: data.profileImageUrl || '', welcomeMessage: data.welcomeMessage || '', ctaButtons: data.ctaButtons || [], sections: data.sections || [] };
}
async function authorSession() {
  const result = await resolveSessionForType('author', { restore: true, read: async () => ({ attributes: await fetchUserAttributes(), auth: await fetchAuthSession() }) });
  const token = result.data.auth.tokens?.idToken?.toString();
  if (!token) throw new Error('Please sign in again.');
  return { attributes: result.data.attributes, headers: { Authorization: 'Bearer ' + token } };
}
function fileBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = () => reject(new Error('Unable to read image'));
    reader.readAsDataURL(file);
  });
}
export default function WelcomePage() {
  const [identity, setIdentity] = useState(null);
  const [value, setValue] = useState(empty);
  const [saved, setSaved] = useState(JSON.stringify(empty));
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [retry, setRetry] = useState(0);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState('');
  const [imageVersion, setImageVersion] = useState(0);
  const lock = useRef(false);
  const errorRef = useRef(null);
  const [version, setVersion] = useState('');
  const [publicUrl, setPublicUrl] = useState('');
  const [conflict, setConflict] = useState(false);
  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  const dirty = ready && JSON.stringify(value) !== saved;

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    setLoading(true); setReady(false); setError(''); setMessage('');
    (async () => {
      try {
        const session = await authorSession();
        const attributes = session.attributes;
        const nextIdentity = {
          userSub: text(attributes.sub),
          firstName: text(attributes['custom:firstname']),
          lastName: text(attributes['custom:lastname']),
          penName: text(attributes['custom:penname']),
          subdomain: text(attributes['custom:subdomain'] || attributes['custom:authorsubdomain']),
        };
        if (!nextIdentity.userSub || !nextIdentity.firstName || !nextIdentity.lastName) throw new Error('Missing author profile');
        const { userSub, firstName, lastName, penName } = nextIdentity;
        const fallback = 'Welcome to ' + (penName || firstName + ' ' + lastName) + "'s Circle";
        const response = await axios.post(SAVE_ENDPOINT, { action: 'load' }, { headers: session.headers, signal: controller.signal });
        if (typeof response.data.exists !== 'boolean') throw new Error('Invalid load response');
        const next = response.data.exists ? normalize(response.data.welcome, fallback) : { ...empty, welcomeHeader: fallback };
        if (!alive) return;
        setVersion(response.data.version || '');
        const url = response.data.publicUrl;
        setPublicUrl(typeof url === 'string' && /^https:\/\/[a-z0-9][a-z0-9-]*\.bookishcircle\.com\/$/.test(url) ? url : '');
        setConflict(false);
        setIdentity(nextIdentity); setValue(next); setSaved(JSON.stringify(next)); setReady(true);
        try {
          sessionStorage.setItem('userSub', userSub);
          sessionStorage.setItem('custom:firstname', firstName);
          sessionStorage.setItem('custom:lastname', lastName);
          sessionStorage.setItem('penName', penName);
        } catch { /* Editing does not require browser storage. */ }
      } catch {
        if (alive) setError('We couldn’t load your saved Welcome Page. Retry before editing so your existing story stays safe. If your session has ended, return to Author Login.');
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; controller.abort(); };
  }, [retry]);

  useEffect(() => {
    if (!dirty && !busy) return;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    const navigate = event => { if (lock.current || (dirty && !window.confirm('Leave this tab and discard your unsaved Welcome Page edits?'))) event.preventDefault(); };
    window.addEventListener('bookish:before-navigation', navigate);
    return () => { window.removeEventListener('beforeunload', warn); window.removeEventListener('bookish:before-navigation', navigate); };
  }, [dirty, busy]);

  function change(next) { setValue(next); setMessage(''); setError(''); }
  async function handleImageUpload(event, index) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !ready || lock.current) return;
    setError(''); setMessage('');
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 2 * 1024 * 1024 || !file.size) {
      setError('Choose a PNG, JPEG, or WebP image up to 2 MB.'); return;
    }
    lock.current = true; setBusy('upload');
    try {
      const session = await authorSession();
      if (session.attributes.sub !== identity.userSub) throw new Error('Account changed');
      const response = await axios.post(SAVE_ENDPOINT, { action: 'upload', contentType: file.type, data: await fileBase64(file) }, { headers: session.headers });
      const finalUrl = response.data.url;
      if (typeof finalUrl !== 'string' || !finalUrl.startsWith('https://bookish-master-data.s3.us-west-2.amazonaws.com/Public/Profiles/' + identity.userSub + '/')) throw new Error('Invalid image address');
      setValue(previous => Number.isInteger(index) ? { ...previous, sections: previous.sections.map((section, i) => i === index ? { ...section, imageUrl: finalUrl } : section) } : { ...previous, profileImageUrl: finalUrl });
      setImageVersion(Date.now()); setMessage('Image uploaded. Save your Welcome Page to keep this change.');
    } catch (e) { setError(typeof e.response?.data?.error === 'string' ? e.response.data.error : 'Your image couldn’t be uploaded. Your other edits are still here. Please try again.'); }
    finally { lock.current = false; setBusy(''); }
  }
  async function handleSave() {
    if (!ready || lock.current) return;
    lock.current = true; setBusy('save'); setError(''); setMessage('');
    const snapshot = { ...value };
    try {
      const session = await authorSession();
      if (session.attributes.sub !== identity.userSub) throw new Error('Account changed');
      const response = await axios.post(SAVE_ENDPOINT, { action: 'save', ...snapshot, version }, { headers: session.headers });
      if (response.data.saved !== true) throw new Error('Save was not confirmed');
      const confirmed = normalize(response.data.welcome, snapshot.welcomeHeader);
      setVersion(response.data.version || ''); setConflict(false);
      setValue(confirmed); setSaved(JSON.stringify(confirmed)); setMessage('Your Welcome Page is saved. Open your public page to check how readers see it.');
    } catch (e) {
      setConflict(e.response?.status === 409);
      const detail = e.response?.data?.error;
      setError(typeof detail === 'string' ? detail : 'We couldn’t save your Welcome Page. Your edits are still here—please try again.');
    }
    finally { lock.current = false; setBusy(''); }
  }
  return <section className="text-bookish-royal">
    <h1 className="font-serif text-3xl font-bold">Welcome readers into your Circle</h1>
    <p className="mt-3">Make the home page of your author website feel like you. Add a welcoming introduction, a photo, and links that invite readers into your stories. Saving updates the content used by your public website.</p>
    <p role="status" className="mt-4">{loading ? 'Loading your saved Welcome Page…' : busy === 'upload' ? 'Uploading your image…' : busy === 'save' ? 'Saving your Welcome Page…' : message || (ready ? dirty ? 'You have unsaved changes. Save before leaving this tab.' : 'Your editor matches the loaded or saved page.' : '')}</p>
    <p role="alert" ref={errorRef} tabIndex={-1} className="mt-3">{error}</p>
    {!loading && !ready && <div className="flex flex-wrap gap-4"><button type="button" className="underline min-h-[44px]" onClick={() => setRetry(previous => previous + 1)}>Retry loading</button><Link className="underline py-3" to="/loginauthor">Return to Author Login</Link></div>}
    {ready && <>
      {conflict && <button type="button" disabled={!!busy} className="underline min-h-[44px]" onClick={() => { if (window.confirm('Reload the saved page? This replaces your current edits. Copy anything you want to keep first.')) setRetry(previous => previous + 1); }}>Reload saved page</button>}
      {publicUrl ? <a className="inline-block underline py-3" href={publicUrl} target="_blank" rel="noopener noreferrer">View public Welcome Page (saved content, opens a new tab)</a> : <p className="mt-3 text-sm">Your public-page link will be available once your author website address is set up.</p>}
      <WelcomeEditor value={value} onChange={change} onImageUpload={handleImageUpload} onSave={handleSave} uploading={busy === 'upload'} saving={busy === 'save'} imageCacheKey={imageVersion} acceptedImageTypes="image/png,image/jpeg,image/webp" />
    </>}
  </section>;
}
